import React, { useCallback, useEffect, useState } from "react";
import Link from "next/link";
import {
  IconButton,
  Link as MuiLink,
  Paper,
  Tooltip,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Edit as EditIcon } from "@material-ui/icons";
import { Table } from "src/apps/commons/dataDisplays";
import ActiveStatus from "src/apps/commons/dataDisplays/components/ActiveStatus";
import useHttpErrorHandler from "src/hooks/useHttpErrorHandler";
import { formatDateTime, useDebounce } from "src/utils";
import * as Api from "../api";
import { IUserProductBrief, UserProductPaginateOptionKey } from "../types";
import { paths } from "../utils";
import { paths as remyndBrandPaths } from "../../remyndBrand/utils";
import ListSearchPanel from "./ListSearchPanel";

export const useStyles = makeStyles(theme => ({
  root: {
    width: "100%",
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(1),
    backgroundColor: theme.palette.background.default
  },
  listTextPrimary: {
    color: theme.palette.text.primary,
    fontSize: "0.75rem",
    fontWeight: "bold"
  },
  listTextSecondary: {
    color: theme.palette.text.secondary,
    fontSize: "0.75rem"
  },
  listTextTertiary: {
    color: theme.palette.text.secondary,
    fontSize: "0.75rem"
  }
}));

type CriteriaType = {
  searchTerm: string;
  verified?: boolean;
  partnerVerified?: boolean;
  partnerId?: PartnerId;
  userDeleted?: boolean;
  requestWarranty?: boolean;
  paginate: IPaginate<UserProductPaginateOptionKey>;
};

type StateTypes = {
  fetching: boolean;
  list: IUserProductBrief[];
  totalCount: number;
};

const HeaderCell = (props: { title: string; subtitle: string }) => (
  <div style={{ lineHeight: "1.2rem", textAlign: "left" }}>
    {props.title}
    <br />
    <Typography variant="caption">{props.subtitle}</Typography>
  </div>
);

const ManageCell = (data: IUserProductBrief, isLoading: boolean) => {
  const { userDeleted } = data;
  if (userDeleted) return <div />;
  return (
    <div>
      {isLoading ? (
        <IconButton
          aria-label="Verify"
          key={`loading_manage_${data.id}`}
          disabled
        >
          <EditIcon />
        </IconButton>
      ) : (
        <Link href={`${paths.edit}?id=${data.id}`} key={`manage_${data.id}`}>
          <a>
            <IconButton aria-label="Verify">
              <Tooltip title="Verify User Product" placement="top">
                <EditIcon />
              </Tooltip>
            </IconButton>
          </a>
        </Link>
      )}
    </div>
  );
};

const ListContainers: React.FC<unknown> = _ => {
  const classes = useStyles();
  const { handleError } = useHttpErrorHandler();

  const [criteria, setCriteria] = useState<CriteriaType>({
    searchTerm: "",
    userDeleted: false,
    paginate: {
      page: 1,
      pageSize: 10,
      order: { optionKey: "LAST_UPDATED_AT", desc: true }
    }
  });
  const {
    searchTerm,
    verified,
    partnerVerified,
    partnerId,
    userDeleted,
    requestWarranty,
    paginate
  } = criteria;
  const { page, pageSize, order } = paginate;
  const orderDesc = order?.desc;
  const orderBy = order?.optionKey;
  const { value: debouncedSearchTerm, debouncing } = useDebounce(
    searchTerm,
    1000
  );

  const [state, setState] = useState<StateTypes>({
    fetching: false,
    list: [],
    totalCount: 0
  });

  const fetchList = useCallback(async () => {
    setState(s => ({ ...s, fetching: true }));
    try {
      const resp = await Api.listUserProducts({
        paginate: { page, pageSize, order },
        verified: verified,
        partnerVerified: partnerVerified,
        partnerId: partnerId,
        userDeleted: userDeleted,
        requestWarranty: requestWarranty,
        searchTerm: debouncedSearchTerm
      });
      const { results, totalCount } = resp.value;
      setState({ fetching: false, list: results, totalCount: totalCount });
    } catch (e) {
      handleError(e);
      setState({ fetching: false, list: [], totalCount: 0 });
    }
  }, [
    handleError,
    verified,
    partnerVerified,
    partnerId,
    userDeleted,
    requestWarranty,
    debouncedSearchTerm,
    page,
    pageSize,
    order
  ]);

  const handleChangePageProps = (
    changedPage: number,
    changedPageSize: number
  ) => {
    setCriteria(s => ({
      ...s,
      paginate: { ...s.paginate, page: changedPage, pageSize: changedPageSize }
    }));
  };

  const handleSort = (
    key: UserProductPaginateOptionKey | "MANAGE" | "USER_INFO"
  ) => {
    if (key !== "MANAGE" && key !== "USER_INFO") {
      const toggleDesc = order?.optionKey === key ? !order?.desc : false;
      setCriteria(d => ({
        ...d,
        paginate: {
          ...d.paginate,
          order: { optionKey: key, desc: toggleDesc }
        }
      }));
    }
  };

  const onChangeSearchTerm = (searchTerm: string) => {
    setCriteria(c => ({ ...c, searchTerm }));
  };
  const onChangeVerified = (verified?: boolean) => {
    setCriteria(c => ({ ...c, verified }));
  };
  const onChangePartnerVerified = (partnerVerified?: boolean) => {
    setCriteria(c => ({ ...c, partnerVerified }));
  };
  const onChangeUserDeleted = (userDeleted?: boolean) => {
    setCriteria(c => ({ ...c, userDeleted }));
  };
  const onChangeRequestWarranty = (requestWarranty?: boolean) => {
    setCriteria(c => ({ ...c, requestWarranty }));
  };
  const onChangePartnerId = (partnerId?: PartnerId) => {
    setCriteria(c => ({ ...c, partnerId }));
  };

  useEffect(() => {
    fetchList();
  }, [fetchList, page, pageSize]);

  const isLoading = state.fetching || debouncing;

  const UserInfoCell = useCallback(
    (data: IUserProductBrief) => {
      const { user } = data;
      const { displayName, email, phoneNumber } = user;
      return (
        <div>
          <div>
            <Typography className={classes.listTextPrimary}>
              {displayName ?? "ไม่ระบุชื่อ"}
            </Typography>
          </div>
          <div>
            <Typography className={classes.listTextSecondary}>
              {email ?? "ไม่ระบุ Email"}
            </Typography>
          </div>
          <div>
            <Typography className={classes.listTextTertiary}>
              {phoneNumber ?? "ไม่ระบุเบอร์โทร"}
            </Typography>
          </div>
        </div>
      );
    },
    [
      classes.listTextPrimary,
      classes.listTextSecondary,
      classes.listTextTertiary
    ]
  );

  const DeletedCell = useCallback(
    (data: IUserProductBrief) => (
      <ActiveStatus
        value={data.userDeleted}
        tooltip={data.userDeletedAt && formatDateTime(data.userDeletedAt)}
      />
    ),
    []
  );

  const VerifiedCell = useCallback(
    (data: IUserProductBrief) => <ActiveStatus value={data.verified} />,
    []
  );
  const PartnerVerifiedCell = useCallback(
    (data: IUserProductBrief) => <ActiveStatus value={data.partnerVerified} />,
    []
  );
  const RequestWarrantyCell = useCallback(
    (data: IUserProductBrief) => <ActiveStatus value={data.requestWarranty} />,
    []
  );

  const ProductNameCell = useCallback((data: IUserProductBrief) => {
    return <strong>{data.productName}</strong>;
  }, []);

  const BrandNameCell = useCallback(
    (data: IUserProductBrief) => {
      if (data.brand) {
        return (
          <Link passHref href={`${remyndBrandPaths.edit}?id=${data.brand.id}`}>
            <MuiLink>
              <strong>{data.brand.label}</strong>
            </MuiLink>
          </Link>
        );
      } else if (data.customBrandName) {
        return (
          <div>
            <div>
              <Typography className={classes.listTextPrimary}>
                {data.customBrandName}
              </Typography>
            </div>
            <div>
              <Typography className={classes.listTextTertiary}>
                {"กรอกเอง"}
              </Typography>
            </div>
          </div>
        );
      } else {
        return "ไม่ระบุ";
      }
    },
    [classes]
  );

  const CategoryCell = useCallback(
    (data: IUserProductBrief) => {
      const { categoryLv1, categoryLv2, categoryLv3 } = data;
      if (!categoryLv1 && !categoryLv2 && !categoryLv3) {
        return "ไม่ระบุ";
      }
      return (
        <div>
          {categoryLv1?.label && (
            <div>
              <Typography className={classes.listTextPrimary}>
                L1: {categoryLv1.label}
              </Typography>
            </div>
          )}
          {categoryLv2?.label && (
            <div>
              <Typography className={classes.listTextSecondary}>
                L2: {categoryLv2.label}
              </Typography>
            </div>
          )}
          {categoryLv3?.label && (
            <div>
              <Typography className={classes.listTextTertiary}>
                L3: {categoryLv3.label}
              </Typography>
            </div>
          )}
        </div>
      );
    },
    [classes]
  );

  return (
    <div>
      <ListSearchPanel
        onChangeSearchTerm={onChangeSearchTerm}
        onChangeVerified={onChangeVerified}
        onChangePartnerVerified={onChangePartnerVerified}
        onChangeUserDeleted={onChangeUserDeleted}
        onChangeRequestWarranty={onChangeRequestWarranty}
        onChangePartnerId={onChangePartnerId}
      />

      <Paper className={classes.root}>
        <Table<
          IUserProductBrief,
          UserProductPaginateOptionKey,
          "MANAGE" | "USER_INFO"
        >
          searchHeight={175}
          isLoading={isLoading}
          columns={[
            {
              id: "MANAGE",
              label: "",
              width: 48,
              style: { padding: "0 16px 0 8px" },
              align: "center",
              renderCell: ManageCell
            },
            {
              id: "VERIFIED",
              label: <HeaderCell title="Verified" subtitle="(Remynd)" />,
              width: 80,
              style: { padding: "0 8px" },
              orderable: true,
              align: "center",
              renderCell: VerifiedCell
            },
            {
              id: "REMYND_UPDATED_AT",
              label: (
                <HeaderCell title="แก้ไขล่าสุดเมื่อ" subtitle="โดย Remynd" />
              ),
              width: 160,
              style: { padding: "0 8px" },
              orderable: true,
              align: "center",
              renderCell: data =>
                data.remyndUpdatedAt != null
                  ? formatDateTime(data.remyndUpdatedAt)
                  : ""
            },
            {
              id: "PARTNER_NAME",
              label: "Partner",
              minWidth: 100,
              orderable: true,
              renderCell: data => data.partner?.label ?? "ไม่ระบุ",
              renderFaded: data => !data.partner
            },
            {
              id: "PARTNER_VERIFIED",
              label: <HeaderCell title="Verified" subtitle="(Partner)" />,
              width: 80,
              style: { padding: "0 8px" },
              orderable: true,
              align: "center",
              renderCell: PartnerVerifiedCell
            },
            {
              id: "PARTNER_UPDATED_AT",
              label: (
                <HeaderCell title="แก้ไขล่าสุดเมื่อ" subtitle="โดย Partner" />
              ),
              width: 160,
              style: { padding: "0 8px" },
              orderable: true,
              align: "center",
              renderCell: data =>
                data.partnerUpdatedAt != null
                  ? formatDateTime(data.partnerUpdatedAt)
                  : ""
            },
            {
              id: "USER_PRODUCT_NAME",
              label: <HeaderCell title="ชื่อสินค้า" subtitle="จาก User" />,
              minWidth: 220,
              orderable: true,
              renderCell: ProductNameCell,
              renderFaded: data => data.userDeleted
            },
            {
              id: "REQUEST_WARRANTY",
              label: <HeaderCell title="ลงทะเบียน" subtitle="จาก User" />,
              width: 110,
              style: { padding: "0 8px" },
              orderable: true,
              align: "center",
              renderCell: RequestWarrantyCell
            },
            {
              id: "USER_BRAND_NAME",
              label: <HeaderCell title="แบรนด์" subtitle="จาก User" />,
              minWidth: 160,
              orderable: true,
              renderCell: BrandNameCell,
              renderFaded: data => !data.brand && !data.customBrandName
            },
            {
              id: "USER_CATEGORY_NAME",
              label: <HeaderCell title="หมวดหมู่" subtitle="จาก User" />,
              minWidth: 220,
              orderable: true,
              renderCell: CategoryCell,
              renderFaded: data =>
                !data.categoryLv1 && !data.categoryLv2 && !data.categoryLv3
            },
            {
              id: "USER_SELLER_NAME",
              label: <HeaderCell title="ร้านค้าที่ซื้อ" subtitle="จาก User" />,
              minWidth: 180,
              orderable: true,
              renderCell: data => data.receipt?.sellerName ?? "ไม่ระบุ",
              renderFaded: data => !data.receipt?.sellerName
            },
            {
              id: "USER_RECEIPT_NO",
              label: <HeaderCell title="เลขที่ใบเสร็จ" subtitle="จาก User" />,
              minWidth: 180,
              orderable: true,
              renderCell: data => data.receipt?.receiptNo ?? "ไม่ระบุ",
              renderFaded: data => !data.receipt?.receiptNo
            },
            {
              id: "USER_INFO",
              label: "ข้อมูล User",
              minWidth: 180,
              renderCell: UserInfoCell
            },
            {
              id: "USER_UPDATED_AT",
              label: (
                <HeaderCell title="แก้ไขล่าสุดเมื่อ" subtitle="โดย User" />
              ),
              width: 160,
              style: { padding: "8px 8px" },
              orderable: true,
              align: "center",
              renderCell: data => formatDateTime(data.userUpdatedAt)
            },
            {
              id: "USER_DELETED",
              label: <HeaderCell title="ลบข้อมูล" subtitle="โดย User" />,
              minWidth: 100,
              style: { padding: "0 8px" },
              orderable: true,
              align: "center",
              renderCell: DeletedCell
            }
          ]}
          datas={state.list}
          page={page}
          pageSize={pageSize}
          totalCount={state.totalCount}
          orderDesc={orderDesc}
          orderBy={orderBy}
          handleChangePageProps={handleChangePageProps}
          handleSort={handleSort}
        />
      </Paper>
    </div>
  );
};

export default ListContainers;
